import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, computed, signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { DateAdapter } from '@angular/material/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSelect } from '@angular/material/select'
import { MatSort } from '@angular/material/sort'

import { TositeKatseleComponentDataResolve } from '../../_angular/_resolvers/tosite.resolve'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { SelvityspyynnonTyyppi, Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { FirestoreTosite } from '@lemon/core-firebase/model/tosite'
import { CurrencyService } from '@lemon/core/service/currency.service'

import { ListausFirestoreKuitti, TositeSelailuListausFirebaseDataSource } from './tosite.listaus.firebase.datasource'

import { combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators'

import { MatDatepicker } from '@angular/material/datepicker'
import { MatDialog } from '@angular/material/dialog'
import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { TositeHakuStateService } from 'app/_angular/service/tosite/tosite-haku-state.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LocalStorageService } from 'app/_jaettu-angular/service/local-storage.service'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { Asiakas } from '../../_jaettu-lemonator/model/asiakas'
import {
  LisaaTositteetTestisettiinDialog,
  LisaaTositteetTestisettiinDialogData,
  LisaaTositteetTestisettiinDialogResult,
} from '../../yllapito/llm-kirjausehdotus-testaus/tosite-luku/lisaa-tositteet-testisettiin.dialog'
import { callLlmTositeLukuTestaus } from '../../yllapito/llm-kirjausehdotus-testaus/tosite-luku/llm-tosite-luku-testaus.client'
import { ryhmitteleSettijasenyydetTositteittain } from '../../yllapito/llm-kirjausehdotus-testaus/tosite-luku/settijasenyys'

interface TestisettiMerkki {
  teksti: string
  vihje: string
}

type TositteidenNaytto = 'kortit' | 'lista' | 'kuvilla'

const TOSITTEIDEN_NAYTOT: TositteidenNaytto[] = ['kortit', 'lista', 'kuvilla']
const TOSITTEIDEN_NAYTTO_AVAIN = 'lemonator.tositteidenNaytto'

export interface Selvitysnippu {
  tyyppi: SelvityspyynnonTyyppi
  kortit: Selvityspyynto[]
  naytettavat: Selvityspyynto[]
  auki: boolean
}

export interface MaksutapaValinta {
  tunniste: number
  nimi: string
  kuva: string | null
  lukumaara: number
}

@Component({
  templateUrl: './tosite.listaus.component.html',
  styleUrls: ['./tosite.listaus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeSelailuListausComponent implements OnInit, OnDestroy {
  @ViewChild('naytaKaikki', { read: MatSelect, static: true }) naytaKaikkiSelect: MatSelect

  @ViewChild(MatSort) set sort(sort: MatSort | undefined) {
    if (!sort || sort === this._sort) {
      return
    }
    this._sort = sort
    const alkusorttaus = this.dataSource.getSort()
    sort.sort({ id: alkusorttaus.active, start: alkusorttaus.direction === 'asc' ? 'asc' : 'desc', disableClear: true })
    sort.sortChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(muutos => this.dataSource.changeSort(muutos))
  }

  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator | undefined) {
    if (!paginator || paginator === this._paginator) {
      return
    }
    this._paginator = paginator
    paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe(page => this.dataSource.changePage(page))
  }

  private ngUnsubscribe = new Subject<void>()
  private _sort: MatSort | null = null
  private _paginator: MatPaginator | null = null

  naytto: TositteidenNaytto = 'kuvilla'
  naytettavatKuititObservable: Observable<ListausFirestoreKuitti[]>
  naytaEiYhtaanObservable: Observable<boolean>

  kuititUriObservable: Observable<string>
  kuititEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  hakuAvoinna = false

  readonly kuukaudenYhteissumma = signal('')
  readonly maksutapaValinnat = signal<MaksutapaValinta[]>([])
  readonly kaikkienLukumaara = signal(0)
  readonly valittuMaksutapa = signal<number | null>(null)
  private readonly _suodatettujenLukumaara = signal(0)

  otsikkoObservable: Observable<string>
  lataaObservable: Observable<boolean>
  rivienMaaraObservable: Observable<number>
  asiakasObservable: Observable<Asiakas>
  onkoLlmTestausKayttajaObservable: Observable<boolean>
  kirjanpidonProjektitObservable: Observable<KirjanpidonProjekti[]>
  readonly projektitMap = signal(new Map<string, KirjanpidonProjekti>())
  readonly naytaProjektit = computed(() => this.projektitMap().size > 0)
  readonly onkoLlmTestausKayttaja = signal(false)
  readonly displayedColumns = computed(() => {
    const sarakkeet = ['pvm', 'toimittaja']
    if (this.naytaProjektit()) {
      sarakkeet.push('projekti')
    }
    sarakkeet.push('lisatiedot', 'maksutapa', 'summa', 'tila')
    if (this.onkoLlmTestausKayttaja()) {
      sarakkeet.push('testisetti')
    }
    return sarakkeet
  })

  readonly selvityspyynnot = signal<Selvityspyynto[]>([])
  readonly selvitysniput = signal<Selvitysnippu[]>([])
  readonly edellisenKuukaudenSelvitettavat = signal(0)
  readonly seuraavanKuukaudenSelvitettavat = signal(0)

  readonly valitutTestisettiin = signal(new Set<string>())
  readonly testisettiMerkit = signal(new Map<string, TestisettiMerkki>())

  private readonly _asiakas = signal<Asiakas | null>(null)
  private readonly _maksutapaNimet = signal(new Map<number, string>())
  private _avatutNiput = new Set<SelvityspyynnonTyyppi>()
  private _testisettiMerkkienAsiakasAvain: string | null = null

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _errorHandler: ErrorHandler,
    private _dateAdapter: DateAdapter<Date>,
    private _currencyService: CurrencyService,
    public dataSource: TositeSelailuListausFirebaseDataSource,
    private _kuittiKatseleComponentDataResolver: TositeKatseleComponentDataResolve,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _dateService: DateService,
    private _dialog: MatDialog,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _tositeUriService: TositeUriService,
    private _kirjanpitoService: KirjanpitoService,
    private _hakuStateService: TositeHakuStateService,
    private _localStorageService: LocalStorageService,
  ) {}

  avaaHaku() {
    this.hakuAvoinna = true
  }

  suljeHaku() {
    this.hakuAvoinna = false
  }

  ngOnInit() {
    const tallennettuNaytto = this._localStorageService.getFromLocalStorage<TositteidenNaytto>(TOSITTEIDEN_NAYTTO_AVAIN)
    if (TOSITTEIDEN_NAYTOT.includes(tallennettuNaytto)) {
      this.naytto = tallennettuNaytto
    }

    if (this._hakuStateService.onkoHakutilaTallennettu(this._router.url)) {
      this.hakuAvoinna = true
    }

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.kuititUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._tositeUriService.annaTositteidenFirestoreCollectionUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.kuititEncodedUriObservable = this.kuititUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLinkLemonaid(uri)
      }),
    )

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable
    this.asiakasObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(asiakas => {
      this._asiakas.set(asiakas)
    })
    this.lataaObservable = this.dataSource.lataaObservable
    this.rivienMaaraObservable = this.dataSource.rivienMaaraObservable

    this.kirjanpidonProjektitObservable = this._kirjanpitoService.kirjanpidonProjektitObservable
    this.kirjanpidonProjektitObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(projektit => {
      this.projektitMap.set(new Map((projektit ?? []).map(projekti => [projekti.avain, projekti])))
    })

    this._asiakasService.nykyisenAsiakkaanKaikkiMaksutavatObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(maksutavat => {
      this._maksutapaNimet.set(new Map((maksutavat ?? []).map(maksutapa => [maksutapa.tunniste, maksutapa.nimi])))
    })

    // Callable vaatii SUPER-roolin (LemonatorAccessLevel.SUPERUSER), joten pelkkä tiimijäsenyys ei riitä eikä toiminto saa näkyä muille.
    this.onkoLlmTestausKayttajaObservable = combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiTaiMuutObservable,
      this._kirjautunutKayttajaService.kirjanpitajaOnSallittuLlmTestausKayttajaObservable,
    ]).pipe(map(([kirjanpitaja, a, b]) => kirjanpitaja?.rooli === KirjanpitajanRooli.SUPER && (a || b)))

    combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable.pipe(map(avainTiedot => avainTiedot?.avain ?? null)), this.onkoLlmTestausKayttajaObservable])
      .pipe(
        distinctUntilChanged(([a1, b1], [a2, b2]) => a1 === a2 && b1 === b2),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(([asiakasAvain, onkoLlmTestausKayttaja]) => {
        this.onkoLlmTestausKayttaja.set(onkoLlmTestausKayttaja)
        this.valitutTestisettiin.set(new Set<string>())
        this.testisettiMerkit.set(new Map<string, TestisettiMerkki>())
        this._testisettiMerkkienAsiakasAvain = onkoLlmTestausKayttaja ? asiakasAvain : null
        if (this._testisettiMerkkienAsiakasAvain) {
          this._lataaTestisettiMerkit(this._testisettiMerkkienAsiakasAvain)
        }
      })

    this.otsikkoObservable = this.dataSource.hakukriteeritObservable.pipe(
      map(kriteerit => {
        if (kriteerit && kriteerit.vuosikk) {
          return this._dateAdapter.getMonthNames('long')[kriteerit.vuosikk.kk] + ' ' + kriteerit.vuosikk.vuosi
        }
        return ''
      }),
    )

    const avoimetSelvityspyynnotObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return of<Selvityspyynto[]>([])
        }
        const polku = this._tositeUriService.annaSelvityspyynnotCollectionUri(avainTiedot.avain)
        return this._firebaseLemonaid.firestoreCollection<Selvityspyynto>(polku).listen()
      }),
      map(pyynnot => (pyynnot ?? []).filter(pyynto => pyynto.tila === 'avoin' && pyynto.poistettu !== true)),
    )

    combineLatest([avoimetSelvityspyynnotObservable, this.dataSource.hakukriteeritObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([kaikki, hakukriteerit]) => {
        const vuosi = hakukriteerit.vuosikk.vuosi
        const kuukausi = hakukriteerit.vuosikk.kk + 1
        const valittuKuukausi = vuosi * 100 + kuukausi

        this.selvityspyynnot.set(kaikki.filter(pyynto => pyynto.pvml?.year === vuosi && pyynto.pvml?.month === kuukausi))
        this.edellisenKuukaudenSelvitettavat.set(kaikki.filter(pyynto => pyynto.pvml && pyynto.pvml.year * 100 + pyynto.pvml.month < valittuKuukausi).length)
        this.seuraavanKuukaudenSelvitettavat.set(kaikki.filter(pyynto => pyynto.pvml && pyynto.pvml.year * 100 + pyynto.pvml.month > valittuKuukausi).length)

        this._paivitaSelvitysniput()
      })

    combineLatest([this.dataSource.kuukaudenKuititObservable, this._asiakasService.nykyisenAsiakkaanKaikkiMaksutavatObservable, this.dataSource.valittuMaksutapaObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([kuitit, maksutavat, valittuMaksutapa]) => {
        const lukumaarat = new Map<number, number>()
        for (const kuitti of kuitit) {
          lukumaarat.set(kuitti.maksutapa, (lukumaarat.get(kuitti.maksutapa) ?? 0) + 1)
        }
        this.valittuMaksutapa.set(valittuMaksutapa)
        this.kaikkienLukumaara.set(kuitit.length)
        this.maksutapaValinnat.set(
          (maksutavat ?? [])
            .filter(maksutapa => lukumaarat.has(maksutapa.tunniste) || maksutapa.tunniste === valittuMaksutapa)
            .map(maksutapa => ({
              tunniste: maksutapa.tunniste,
              nimi: maksutapa.nimi,
              kuva: maksutapa.base64Kuva ? `data:image/png;base64,${maksutapa.base64Kuva}` : null,
              lukumaara: lukumaarat.get(maksutapa.tunniste) ?? 0,
            })),
        )
      })

    this.dataSource.suodatetutKuititObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(kuitit => {
      this._suodatettujenLukumaara.set(kuitit.length)
      this.kuukaudenYhteissumma.set(this._laskeYhteissumma(kuitit.map(kuitti => kuitti.sum ?? kuitti.summa ?? 0)))
    })

    this.naytettavatKuititObservable = this.dataSource.suodatetutKuititObservable

    this.naytaEiYhtaanObservable = combineLatest([this.dataSource.rivienMaaraObservable, this.dataSource.lataaObservable]).pipe(
      map(([rivienMaara, lataa]) => {
        if (lataa) {
          return false
        }
        return rivienMaara < 1
      }),
    )

    this.naytaKaikkiSelect.value = this.dataSource.getNaytaPoistetut()
    this.naytaKaikkiSelect.selectionChange
      .asObservable()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(selectionChange => {
        this.dataSource.changeNaytaPoistetut(selectionChange.value)
      })
  }

  vaihdaNaytto(naytto: TositteidenNaytto) {
    this.naytto = naytto
    this._localStorageService.setToLocalStorage(TOSITTEIDEN_NAYTTO_AVAIN, naytto)
  }

  kuvanLataaminenValmis(kuitti: ListausFirestoreKuitti) {
    kuitti.ladataan = false
  }

  kuvanLataaminenEpaonnistui(kuitti: ListausFirestoreKuitti) {
    kuitti.ladataan = false
    kuitti.kuvaEpaonnistui = true
  }

  toggleNaytaLisaa(event: Event, kuitti: ListausFirestoreKuitti) {
    event.preventDefault()
    event.stopPropagation()

    kuitti.naytaLisaa = !kuitti.naytaLisaa
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  get tositteidenKokonaismaara(): number {
    if (this.valittuMaksutapa() !== null) {
      return this._suodatettujenLukumaara()
    }
    return this._suodatettujenLukumaara() + this.selvityspyynnot().length
  }

  valitseMaksutapa(maksutapa: number | null) {
    this.dataSource.vaihdaValittuMaksutapa(maksutapa)
  }

  get asiakas(): Asiakas | null {
    return this._asiakas()
  }

  maksutapaNimi(tosite: FirestoreTosite): string {
    return this._maksutapaNimet().get(tosite.maksutapa) ?? ''
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    if (normalizedMonth) {
      this.dataSource.changeVuosiKk(normalizedMonth.getFullYear(), normalizedMonth.getMonth())
    }
    datepicker?.close()
  }

  edellinenKuukausi(event: MouseEvent) {
    const vuosiKk = this.dataSource.getVuosiKk()
    const vuosi = vuosiKk.kk < 1 ? vuosiKk.vuosi - 1 : vuosiKk.vuosi
    const kuukausi = vuosiKk.kk < 1 ? 11 : vuosiKk.kk - 1
    this.dataSource.changeVuosiKk(vuosi, kuukausi)
    event.stopPropagation()
    event.preventDefault()
  }

  seuraavaKuukausi(event: MouseEvent) {
    const vuosiKk = this.dataSource.getVuosiKk()
    const vuosi = vuosiKk.kk > 10 ? vuosiKk.vuosi + 1 : vuosiKk.vuosi
    const kuukausi = vuosiKk.kk > 10 ? 0 : vuosiKk.kk + 1
    this.dataSource.changeVuosiKk(vuosi, kuukausi)
    event.stopPropagation()
    event.preventDefault()
  }

  kuvillaRiviNappain(event: KeyboardEvent, tosite: FirestoreTosite) {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return
    }
    event.preventDefault()
    this.katsele(tosite)
  }

  katsele(tosite: FirestoreTosite) {
    this._asiakasService.nykyinenAsiakasObservable.pipe(take(1)).subscribe({
      next: asiakas => {
        if (asiakas) {
          this._kuittiKatseleComponentDataResolver.asetaOlemassaolevaData({ tosite: tosite, asiakas: asiakas })
          this._router.navigate(['asiakkaat', asiakas.avain, 'tositteet', 'selaa', tosite.avain])
        }
      },
      error: error => {
        this._errorHandler.handleError(error)
      },
    })
  }

  avaaSelvitysnippu(tyyppi: SelvityspyynnonTyyppi) {
    this._avatutNiput.add(tyyppi)
    this._paivitaSelvitysniput()
  }

  niputaSelvitettavat(tyyppi: SelvityspyynnonTyyppi) {
    this._avatutNiput.delete(tyyppi)
    this._paivitaSelvitysniput()
  }

  private _paivitaSelvitysniput() {
    const jarjestetyt = [...this.selvityspyynnot()].sort((a, b) => (this._dateService.localDateToNumber(b.pvml) ?? 0) - (this._dateService.localDateToNumber(a.pvml) ?? 0))
    const niput: Selvitysnippu[] = []
    for (const pyynto of jarjestetyt) {
      const tyyppi: SelvityspyynnonTyyppi = pyynto.tyyppi === 'kuititon' ? 'kuititon' : 'lisatietopyynto'
      const nippu = niput.find(n => n.tyyppi === tyyppi)
      if (nippu) {
        nippu.kortit.push(pyynto)
      } else {
        niput.push({ tyyppi, kortit: [pyynto], naytettavat: [], auki: false })
      }
    }
    for (const nippu of niput) {
      if (nippu.kortit.length < 2) {
        this._avatutNiput.delete(nippu.tyyppi)
      }
      nippu.auki = this._avatutNiput.has(nippu.tyyppi)
      nippu.naytettavat = nippu.auki ? nippu.kortit : nippu.kortit.slice(0, 1)
    }
    this.selvitysniput.set(niput)
  }

  private _laskeYhteissumma(summat: number[]): string {
    if (summat.length === 0) {
      return ''
    }
    const yhteensa = summat.reduce((acc, n) => acc + n, 0)
    return this._currencyService.formatoiRaha(yhteensa, 'EUR', 'fi')
  }

  toggleTestisettiValinta(tositeAvain: string, valittu: boolean) {
    this.valitutTestisettiin.update(valitut => {
      const uudet = new Set(valitut)
      if (valittu) {
        uudet.add(tositeAvain)
      } else {
        uudet.delete(tositeAvain)
      }
      return uudet
    })
  }

  tyhjennaTestisettiValinta() {
    this.valitutTestisettiin.set(new Set<string>())
  }

  async lisaaValitutTestisettiin() {
    if (!this.valitutTestisettiin().size) {
      return
    }
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable.pipe(take(1)))
    if (!asiakas) {
      return
    }
    const data: LisaaTositteetTestisettiinDialogData = {
      kohteet: Array.from(this.valitutTestisettiin()).map(tositeAvain => ({
        tyyppi: 'lemonaid',
        asiakasAvain: asiakas.avain,
        asiakasId: `${asiakas.asiakasId}`,
        asiakasNimi: asiakas.nimi,
        tositeAvain,
      })),
    }
    const tulos = await firstValueFrom(
      this._dialog
        .open<LisaaTositteetTestisettiinDialog, LisaaTositteetTestisettiinDialogData, LisaaTositteetTestisettiinDialogResult>(LisaaTositteetTestisettiinDialog, {
          data,
          maxWidth: '95vw',
          disableClose: true,
        })
        .afterClosed(),
    )
    if (tulos?.lisatty) {
      this.tyhjennaTestisettiValinta()
      if (this._testisettiMerkkienAsiakasAvain) {
        this._lataaTestisettiMerkit(this._testisettiMerkkienAsiakasAvain)
      }
    }
  }

  private async _lataaTestisettiMerkit(asiakasAvain: string) {
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeAsiakkaanSettijasenyydet', { asiakasAvain })
      if (vastaus.e || this._testisettiMerkkienAsiakasAvain !== asiakasAvain) {
        return
      }
      const tositteenSetit = ryhmitteleSettijasenyydetTositteittain(vastaus.jasenyydet)
      const merkit = new Map<string, TestisettiMerkki>()
      for (const [tositeAvain, setit] of tositteenSetit) {
        const nimet = setit.map(s => s.settiNimi)
        merkit.set(tositeAvain, {
          teksti: nimet.length === 1 ? nimet[0] : `${nimet.length} testisetissä`,
          vihje: `Testisetissä: ${nimet.join(', ')}`,
        })
      }
      this.testisettiMerkit.set(merkit)
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }
}
